import { ENDPOINT } from '~/constants/endpoints';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type DeleteUnitMappingPayload,
  type DeleteUnitMappingResponse,
} from './types';
import { zodDeleteUnitMappingEndpointSchema } from './validations';

/**
 * Deletes an existing unit mapping.
 *
 * @param {Object} unitMappingId - The if of the unit mapping to delete.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Units/operation/delete_unit_mapping_units_unit_mapping__id__delete
 */
export const deleteUnitMapping = async (
  unitMappingId: DeleteUnitMappingPayload,
) => {
  try {
    const response = await vestigasApi
      .delete(ENDPOINT.UNITS.DELETE_MAPPING(unitMappingId), {
        validate: {
          pathSegments: zodDeleteUnitMappingEndpointSchema,
        },
      })
      .json<DeleteUnitMappingResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error deleting unit mapping',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.unit.deleteUnitMapping.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.unit.deleteUnitMapping.error.generic, {
            ns: 'data',
          });
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
